/**
 * Shared gate state for automation consent (TMMAPP-0027).
 * For a profiled person, Connect / Import stay blocked until `reviewedAt` is known and
 * non-null — fail closed while loading or when the catalogue GET fails.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { automationApi, type AutomationCatalog } from '@/api/automation';
import { useMe } from '@/session/meHooks';

export interface AutomationGateState {
  /** True while the catalogue is loading for a profiled person. */
  loading: boolean;
  /**
   * True when evaluation paths must not run: loading, fetch failure (`catalog === null`),
   * or `reviewedAt === null`. False only when `reviewedAt` is a known non-null instant.
   */
  needsConsent: boolean;
  catalog: AutomationCatalog | null;
  reload: () => Promise<AutomationCatalog | null>;
}

export function useAutomationGate(enabled = true): AutomationGateState {
  const me = useMe();
  const hasProfile = enabled && me.user.id !== null;
  const [catalog, setCatalog] = useState<AutomationCatalog | null>(null);
  const [loading, setLoading] = useState(hasProfile);
  const started = useRef(false);

  const reload = useCallback(async () => {
    if (!hasProfile) {
      setCatalog(null);
      setLoading(false);
      return null;
    }
    try {
      const next = await automationApi.get();
      setCatalog(next);
      setLoading(false);
      return next;
    } catch {
      setCatalog(null);
      setLoading(false);
      return null;
    }
  }, [hasProfile]);

  useEffect(() => {
    if (!hasProfile) {
      started.current = false;
      queueMicrotask(() => {
        setCatalog(null);
        setLoading(false);
      });
      return;
    }
    if (started.current) return;
    started.current = true;
    queueMicrotask(() => {
      void reload();
    });
  }, [hasProfile, reload]);

  // Fail closed: block until reviewedAt is known and non-null.
  const needsConsent = hasProfile && (loading || catalog === null || catalog.reviewedAt === null);

  return { loading, needsConsent, catalog, reload };
}
